/**
 * Pure helper for the directory-integration Edit form's credential rotation.
 *
 * On edit the stored secret is never read back (GetDirectoryIntegration omits
 * it), so the form starts blank and only the fields the admin actually
 * re-enters should be sent. The backend MERGES the submitted keys onto the
 * existing encrypted blob (see `update_directory_integration`), so omitting a
 * field preserves its stored secret and a sent field rotates it.
 */

/**
 * Build the credentials patch for an edit-mode update.
 *
 * Returns an object containing only the credential fields the admin filled in
 * (whitespace-trimmed, blanks dropped), or `undefined` when nothing was
 * entered — the honest "no rotation" signal, so the update stays a pure
 * name/config edit and the backend leaves every stored secret untouched.
 *
 * Only keys declared in `credentialFields` are considered, so a stray value in
 * the `credentials` state (e.g. left over from a provider switch) can never
 * leak onto the wire.
 *
 * @param {ReadonlyArray<{key: string}>} credentialFields provider credential field specs
 * @param {Record<string, string>} credentials current form values keyed by field key
 * @returns {Record<string, string> | undefined}
 */
export function buildCredentialPatch(credentialFields, credentials) {
    const patch = {};
    for (const f of credentialFields) {
        const v = (credentials[f.key] ?? "").trim();
        if (v) patch[f.key] = v;
    }
    return Object.keys(patch).length > 0 ? patch : undefined;
}
